import * as L from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import {
    API_BASE_URL,
    DEBUG,
    TRACKING_API_BASE_URL,
    shouldSendCloudFrontSecretForUrl,
} from './config.js';

declare const __APP_VERSION__: string;
const VERSION = __APP_VERSION__;

function normalizePathname(pathname: string): string {
    if (!pathname) return '/';
    const t = pathname.trim();
    if (t === '' || t === '/') return '/';
    return t.replace(/\/+$/, '') || '/';
}

const MAX_TRACKING_CODE_LEN = 128;

function normalizeTrackingCodeSegment(segment: string): string | null {
    const t = segment.trim();
    if (!t || t.length > MAX_TRACKING_CODE_LEN) return null;
    return t;
}

/** 路徑：/track/{追蹤碼}、/web/track/{追蹤碼}；碼由後端簽發，不寫死範例；允許 URL 編碼。 */
function getTrackingCodeFromPath(pathname: string): string | null {
    const p = normalizePathname(pathname);
    const matched = p.match(/^\/(?:web\/)?track\/([^/?#]+)$/);
    if (!matched?.[1]) return null;
    try {
        return normalizeTrackingCodeSegment(decodeURIComponent(matched[1]));
    } catch {
        return normalizeTrackingCodeSegment(matched[1]);
    }
}

/** 從完整 href 再解一次（避免僅依 pathname 遺漏） */
function resolveTrackingCodeFromLocation(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const href = window.location.href || '';
        const m = href.match(/\/(?:web\/)?track\/([^/?#]+)(?:\?|#|\/|$)/);
        if (!m?.[1]) return null;
        try {
            return normalizeTrackingCodeSegment(decodeURIComponent(m[1]));
        } catch {
            return normalizeTrackingCodeSegment(m[1]);
        }
    } catch {
        return null;
    }
}

/** 查詢字串：?trackingCode= 或 ?code=（後端／通知常用） */
function getTrackingCodeFromQuery(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const q = new URLSearchParams(window.location.search);
        const raw = q.get('trackingCode') ?? q.get('code');
        if (!raw) return null;
        try {
            return normalizeTrackingCodeSegment(decodeURIComponent(raw));
        } catch {
            return normalizeTrackingCodeSegment(raw);
        }
    } catch {
        return null;
    }
}

function sanitizeErrorMessage(error: unknown): string {
    if (!error) return '發生未知錯誤，請稍後再試';
    const msg = error instanceof Error ? error.message : String(error);
    if (DEBUG) return msg;
    if (
        msg.includes('ECONNREFUSED') ||
        msg.includes('ENOTFOUND') ||
        msg.includes('NetworkError') ||
        msg.includes('Failed to fetch')
    ) {
        return '發生錯誤，請稍後再試';
    }
    if (msg.includes('CORS') || msg.includes('cross-origin')) {
        return '發生錯誤，請稍後再試';
    }
    if (msg.includes('timeout') || msg.includes('TIMEOUT')) {
        return '發生錯誤，請稍後再試';
    }
    if (msg.length > 100 || msg.includes('at ') || msg.includes('Error:')) {
        return '發生錯誤，請稍後再試';
    }
    return msg;
}

function getBackendErrorMessage(responseData: unknown, fallback: string): string {
    if (!responseData || typeof responseData !== 'object') return fallback;
    const o = responseData as { errors?: { message?: string }; message?: string };
    const errorMsg = o.errors?.message ?? o.message;
    return errorMsg ? String(errorMsg) : fallback;
}

function normalizeTrackingDisplayValue(value: unknown, fallback = '-'): string {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
}

function escapeHtmlAttribute(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/\r?\n/g, ' ');
}

function toFiniteNumber(value: unknown): number | null {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

/** GetLastLocationEta 等：Body 為 module + payload.data（追蹤欄位在 data） */
function pickTrackingData(payload: unknown): unknown {
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as { payload?: { data?: unknown }; data?: unknown };
    return o.payload?.data ?? o.data ?? payload;
}

async function fetchTrackingByCode(trackingCode: string): Promise<Record<string, unknown>> {
    /**
     * GetLastLocationEta — GET /tracking/last-location-eta
     * Query：attributes, debug, examine, trackingCode（公開、無 Access Token）
     */
    const lastLocationEtaParams = new URLSearchParams({
        attributes: 'module,payload,errors,data',
        debug: 'false',
        examine: 'false',
        trackingCode,
        _t: Date.now().toString(),
    });

    const legacyParams = new URLSearchParams({
        attributes: 'module,errors,data',
        debug: 'false',
        examine: 'false',
        trackingCode,
        _t: Date.now().toString(),
    });

    const base = TRACKING_API_BASE_URL || API_BASE_URL;
    const candidates = [
        `${base}/tracking/last-location-eta?${lastLocationEtaParams.toString()}`,
        `${base}/public/tracking?${legacyParams.toString()}`,
        `${base}/public/track?${legacyParams.toString()}`,
        `${base}/tracking?${legacyParams.toString()}`,
        `${base}/track?${legacyParams.toString()}`,
    ];

    let lastError = '找不到可用的追蹤 API';
    for (const apiUrl of candidates) {
        const headers: Record<string, string> = {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
        };
        if (shouldSendCloudFrontSecretForUrl(apiUrl)) {
            headers['X-CloudFront-Secret'] = 'air_web_api';
        }
        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers,
                credentials: 'omit',
                cache: 'no-store',
            });
            const contentType = response.headers.get('Content-Type') || '';
            let responseData: unknown = null;
            if (contentType.includes('application/json')) {
                responseData = await response.json();
            } else {
                const textResponse = await response.text();
                try {
                    responseData = JSON.parse(textResponse) as unknown;
                } catch {
                    lastError = `追蹤 API 回應格式錯誤（HTTP ${response.status}）`;
                    continue;
                }
            }
            if (!response.ok) {
                lastError = getBackendErrorMessage(
                    responseData,
                    `HTTP 錯誤: ${response.status}`,
                );
                continue;
            }
            const trackingData = pickTrackingData(responseData) as Record<string, unknown> | null;
            if (!trackingData || typeof trackingData !== 'object') {
                lastError = '追蹤資料為空';
                continue;
            }
            return trackingData;
        } catch (error) {
            lastError = sanitizeErrorMessage(error);
        }
    }
    throw new Error(lastError);
}

const trackingErrorDiv = document.getElementById('trackingError') as HTMLDivElement | null;
const trackingLoadingDiv = document.getElementById('trackingLoading') as HTMLDivElement | null;
const trackingContentDiv = document.getElementById('trackingContent') as HTMLDivElement | null;
const whereTrackingLandingDiv = document.getElementById(
    'whereTrackingLanding',
) as HTMLDivElement | null;
const trackingCodeValueSpan = document.getElementById('trackingCodeValue') as HTMLElement | null;
const trackingDestinationCodeSpan = document.getElementById(
    'trackingDestinationCode',
) as HTMLElement | null;
const trackingDestinationNameSpan = document.getElementById(
    'trackingDestinationName',
) as HTMLElement | null;
const trackingEtaRangeSpan = document.getElementById('trackingEtaRange') as HTMLElement | null;
const trackingCustomerNameSpan = document.getElementById(
    'trackingCustomerName',
) as HTMLElement | null;
const trackingHeaderCustomerNameSpan = document.getElementById(
    'trackingHeaderCustomerName',
) as HTMLElement | null;
const trackingDeliveryStatusSpan = document.getElementById(
    'trackingDeliveryStatus',
) as HTMLElement | null;
const trackingLastReportedSpan = document.getElementById(
    'trackingLastReported',
) as HTMLElement | null;
const trackingLastLatLngSpan = document.getElementById(
    'trackingLastLatLng',
) as HTMLElement | null;
const trackingMapWrapEl = document.getElementById('trackingMapWrap') as HTMLDivElement | null;
const trackingMapEl = document.getElementById('trackingMap') as HTMLDivElement | null;
const versionInfoDiv = document.getElementById('versionInfo') as HTMLDivElement | null;

let trackingMapInstance: LeafletMap | null = null;

function resetTrackingHeaderCustomer(): void {
    if (!trackingHeaderCustomerNameSpan) return;
    trackingHeaderCustomerNameSpan.textContent = '';
    trackingHeaderCustomerNameSpan.hidden = true;
}

function destroyTrackingMap(): void {
    if (trackingMapInstance) {
        trackingMapInstance.remove();
        trackingMapInstance = null;
    }
}

function hideTrackingMapUi(): void {
    destroyTrackingMap();
    if (trackingMapWrapEl) trackingMapWrapEl.style.display = 'none';
}

/** OpenStreetMap 圖資，見 https://leafletjs.com/ */
function renderTrackingMap(
    lat: number,
    lng: number,
    destination?: { lat: number; lng: number; name?: string },
): void {
    destroyTrackingMap();
    if (!trackingMapWrapEl || !trackingMapEl) return;
    trackingMapWrapEl.style.display = 'block';
    requestAnimationFrame(() => {
        if (!trackingMapEl) return;
        const map = L.map(trackingMapEl, { scrollWheelZoom: true }).setView([lat, lng], 15);
        trackingMapInstance = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> 貢獻者',
            maxZoom: 19,
        }).addTo(map);

        const isOverlapping =
            !!destination &&
            Math.abs(lat - destination.lat) < 0.00001 &&
            Math.abs(lng - destination.lng) < 0.00001;

        // 若司機點與客戶點重疊，微偏移避免其中一個被完全遮住
        const driverLat = isOverlapping ? lat + 0.00012 : lat;
        const driverLng = isOverlapping ? lng - 0.00012 : lng;
        const destinationLat = isOverlapping && destination ? destination.lat - 0.00012 : destination?.lat;
        const destinationLng = isOverlapping && destination ? destination.lng + 0.00012 : destination?.lng;

        const driverIcon = L.divIcon({
            className: '',
            html: '<div class="map-pin map-pin-driver" aria-label="司機位置">🚗</div>',
            iconSize: [50, 50],
            iconAnchor: [34, 34],
        });
        const popupKeepOpen = { autoClose: false };

        const driverMarker = L.marker([driverLat, driverLng], {
            icon: driverIcon,
            zIndexOffset: 1000,
        })
            .addTo(map)
            .bindPopup('司機位置', popupKeepOpen);

        let destinationMarker: L.Marker | null = null;
        if (destination && destinationLat !== undefined && destinationLng !== undefined) {
            const destinationPopupText = normalizeTrackingDisplayValue(destination.name, '—');
            const destinationPopupEl = document.createElement('div');
            destinationPopupEl.textContent = destinationPopupText;
            const destinationAria =
                destinationPopupText === '—'
                    ? '客戶位置'
                    : escapeHtmlAttribute(destinationPopupText);
            const destinationIcon = L.divIcon({
                className: '',
                html: `<div class="map-pin map-pin-destination" aria-label="${destinationAria}">🏠</div>`,
                iconSize: [50, 50],
                iconAnchor: [34, 34],
            });
            destinationMarker = L.marker([destinationLat, destinationLng], {
                icon: destinationIcon,
                zIndexOffset: 500,
            })
                .addTo(map)
                .bindPopup(destinationPopupEl, popupKeepOpen);

            map.fitBounds(
                [
                    [driverLat, driverLng],
                    [destinationLat, destinationLng],
                ],
                { padding: [32, 32] },
            );
        }
        driverMarker.openPopup();
        destinationMarker?.openPopup();
        map.invalidateSize();
    });
}

function setTrackingError(message: string): void {
    if (!trackingErrorDiv || !trackingLoadingDiv || !trackingContentDiv) return;
    hideTrackingMapUi();
    resetTrackingHeaderCustomer();
    if (whereTrackingLandingDiv) whereTrackingLandingDiv.style.display = 'none';
    trackingLoadingDiv.style.display = 'none';
    trackingContentDiv.style.display = 'none';
    trackingErrorDiv.textContent = message;
    trackingErrorDiv.style.display = 'block';
}

function showLanding(): void {
    hideTrackingMapUi();
    resetTrackingHeaderCustomer();
    if (whereTrackingLandingDiv) whereTrackingLandingDiv.style.display = 'block';
    if (trackingErrorDiv) trackingErrorDiv.style.display = 'none';
    if (trackingLoadingDiv) trackingLoadingDiv.style.display = 'none';
    if (trackingContentDiv) trackingContentDiv.style.display = 'none';
}

async function showTracking(trackingCode: string): Promise<void> {
    if (whereTrackingLandingDiv) whereTrackingLandingDiv.style.display = 'none';
    hideTrackingMapUi();

    if (
        !trackingErrorDiv ||
        !trackingLoadingDiv ||
        !trackingContentDiv ||
        !trackingEtaRangeSpan ||
        !trackingLastReportedSpan
    ) {
        setTrackingError('頁面載入異常，請重新整理後再試。');
        return;
    }

    trackingErrorDiv.style.display = 'none';
    trackingLoadingDiv.style.display = 'block';
    trackingContentDiv.style.display = 'none';
    resetTrackingHeaderCustomer();
    if (trackingCodeValueSpan) trackingCodeValueSpan.textContent = trackingCode;

    try {
        const trackingData = await fetchTrackingByCode(trackingCode);
        const destinationCode =
            trackingData.destinationCode ??
            trackingData.orderCode ??
            trackingData.orderNo ??
            trackingData.orderId;
        const destinationName =
            trackingData.destinationName ??
            trackingData.customerName ??
            trackingData.siteName ??
            trackingData.receiverName;
        const etaRange =
            trackingData.etaRange ??
            trackingData.eta ??
            trackingData.estimatedArrivalRange ??
            trackingData.estimatedArrivalAt;
        const deliveryStatus =
            trackingData.deliveryStatus ??
            trackingData.status ??
            trackingData.currentStatus;
        const lastReportedAt =
            trackingData.lastReportedAt ??
            trackingData.updatedAt ??
            trackingData.reportedAt;

        const lat = toFiniteNumber(trackingData.lat);
        const lng = toFiniteNumber(trackingData.lng);
        const isEtaReliable = trackingData.isEtaReliable;
        const destinationLat = toFiniteNumber(
            trackingData.destinationLat ?? trackingData.customerLat ?? trackingData.siteLat,
        );
        const destinationLng = toFiniteNumber(
            trackingData.destinationLng ?? trackingData.customerLng ?? trackingData.siteLng,
        );

        if (trackingDestinationCodeSpan) {
            trackingDestinationCodeSpan.textContent = normalizeTrackingDisplayValue(
                destinationCode,
                '-',
            );
        }
        if (trackingDestinationNameSpan) {
            trackingDestinationNameSpan.textContent = normalizeTrackingDisplayValue(
                destinationName,
                '-',
            );
        }
        if (trackingCustomerNameSpan) {
            trackingCustomerNameSpan.textContent = normalizeTrackingDisplayValue(
                destinationName,
                '—',
            );
        }
        if (trackingHeaderCustomerNameSpan) {
            trackingHeaderCustomerNameSpan.textContent = normalizeTrackingDisplayValue(
                destinationName,
                '—',
            );
            trackingHeaderCustomerNameSpan.hidden = false;
        }

        let etaText = normalizeTrackingDisplayValue(etaRange, '—');
        if (isEtaReliable === false && etaText !== '—') {
            etaText = `${etaText}（預估區間參考價值較低）`;
        }
        trackingEtaRangeSpan.textContent = etaText;

        if (trackingDeliveryStatusSpan) {
            trackingDeliveryStatusSpan.textContent = normalizeTrackingDisplayValue(
                deliveryStatus,
                '—',
            );
        }
        trackingLastReportedSpan.textContent = lastReportedAt
            ? new Date(String(lastReportedAt)).toLocaleString('zh-TW')
            : '—';

        if (trackingLastLatLngSpan) {
            if (lat !== null && lng !== null) {
                trackingLastLatLngSpan.textContent = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
            } else {
                trackingLastLatLngSpan.textContent = '—';
            }
        }

        trackingLoadingDiv.style.display = 'none';
        trackingContentDiv.style.display = 'block';

        if (lat !== null && lng !== null) {
            const destinationPoint =
                destinationLat !== null && destinationLng !== null
                    ? {
                          lat: destinationLat,
                          lng: destinationLng,
                          name: destinationName ? String(destinationName) : undefined,
                      }
                    : undefined;
            renderTrackingMap(lat, lng, destinationPoint);
        } else {
            hideTrackingMapUi();
        }
    } catch (error) {
        const message = sanitizeErrorMessage(error) || '追蹤連結無效或已過期';
        setTrackingError(message);
    }
}

function route(): void {
    const pathnameOnly = normalizePathname(window.location.pathname);
    let code = getTrackingCodeFromPath(pathnameOnly);
    if (!code) code = resolveTrackingCodeFromLocation();
    if (!code) code = getTrackingCodeFromQuery();

    if (code) {
        void showTracking(code);
    } else if (/\/(?:web\/)?track\//.test(pathnameOnly)) {
        if (whereTrackingLandingDiv) whereTrackingLandingDiv.style.display = 'none';
        setTrackingError('無法解析追蹤連結，請重新從通知開啟。');
    } else {
        showLanding();
    }
}

(function init(): void {
    if (versionInfoDiv) {
        versionInfoDiv.textContent = `v${VERSION}`;
        versionInfoDiv.style.display = 'block';
    }
    route();
})();

window.addEventListener('popstate', () => route());
